/**
 * Global API utilities: timeouts, retries, chunk error recovery, diagnostics.
 */

import { getApiPendingRequests, getApiStuckRequests } from "@/lib/api-client";

const IS_DEV = import.meta.env.DEV;

// ── Request diagnostics ──
interface PendingRequest { label: string; start: number; requestId?: string; routeTag?: string; }
const pending = new Map<string, PendingRequest>();

function logDev(...args: unknown[]) {
  if (IS_DEV) console.log("[api]", ...args);
}

export function getPendingRequests(): PendingRequest[] {
  return [
    ...Array.from(pending.values()),
    ...getApiPendingRequests().map((r) => ({
      label: r.label,
      start: r.start,
      requestId: r.requestId,
      routeTag: r.routeTag,
    })),
  ];
}

export function getStuckRequests(thresholdMs = 10_000): PendingRequest[] {
  const now = Date.now();
  return [
    ...getPendingRequests().filter((r) => now - r.start > thresholdMs),
    ...getApiStuckRequests(thresholdMs),
  ];
}

// ── Timeout wrapper for Supabase query builders ──
export function withTimeout<T>(
  thenable: PromiseLike<T>,
  ms: number,
  label: string
): Promise<T> {
  const id = `${label}-${Date.now()}`;
  pending.set(id, { label, start: Date.now() });
  logDev(`→ ${label}`);

  return Promise.race([
    Promise.resolve(thenable).finally(() => {
      pending.delete(id);
      logDev(`✓ ${label}`);
    }),
    new Promise<never>((_, reject) =>
      setTimeout(() => {
        pending.delete(id);
        logDev(`✗ ${label} (timeout ${ms}ms)`);
        reject(new Error(`${label} timed out after ${ms / 1000}s`));
      }, ms)
    ),
  ]);
}

// ── Retry with exponential backoff ──
const RETRYABLE_STATUS = new Set([429, 502, 503, 504]);

export async function withRetry<T>(
  fn: () => Promise<T>,
  opts: { maxRetries?: number; label?: string } = {}
): Promise<T> {
  const { maxRetries = 2, label = "request" } = opts;
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err: any) {
      lastError = err;
      const status = err?.status ?? err?.code;
      const isRetryable =
        err.message?.includes("timed out") ||
        err.message?.includes("Failed to fetch") ||
        err.message?.includes("NetworkError") ||
        (typeof status === "number" && RETRYABLE_STATUS.has(status));

      if (!isRetryable || attempt >= maxRetries) break;

      const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
      logDev(`↻ ${label} retry ${attempt + 1}/${maxRetries} in ${delay}ms`);
      await new Promise(r => setTimeout(r, delay));
    }
  }
  throw lastError;
}

// ── Chunk / dynamic import error recovery ──
let chunkReloadAttempted = false;

export function setupChunkErrorRecovery() {
  window.addEventListener("error", (e) => {
    const msg = e.message || "";
    if (
      (msg.includes("Failed to fetch dynamically imported module") ||
       msg.includes("Loading chunk") ||
       msg.includes("Loading CSS chunk")) &&
      !chunkReloadAttempted
    ) {
      chunkReloadAttempted = true;
      console.warn("[api] Stale chunk detected, reloading...");
      // Clear caches and reload
      if ("caches" in window) {
        caches.keys().then(names => names.forEach(n => caches.delete(n)));
      }
      window.location.reload();
    }
  });

  window.addEventListener("unhandledrejection", (e) => {
    const msg = String(e.reason?.message || e.reason || "");
    if (
      (msg.includes("Failed to fetch dynamically imported module") ||
       msg.includes("Loading chunk")) &&
      !chunkReloadAttempted
    ) {
      chunkReloadAttempted = true;
      console.warn("[api] Stale chunk (unhandled rejection), reloading...");
      window.location.reload();
    }
  });
}

// ── Edge function call with timeout ──
import { supabase } from "@/integrations/supabase/client";

export const DEBUG_DISABLE_LOVABLE_BRIDGE = "DEBUG_DISABLE_LOVABLE_BRIDGE";

// ── Safe postMessage wrapper ──
const ALLOWED_ORIGINS = new Set([
  "https://lovable.dev",
  "https://lovable.app",
  "https://preview--padelcorner.lovable.app",
  "https://padelcorner.lovable.app",
  "https://padelcorner.lv",
  "https://www.padelcorner.lv",
]);

// Add any lovable subdomain pattern
function isAllowedOrigin(origin: string): boolean {
  if (ALLOWED_ORIGINS.has(origin)) return true;
  try {
    const url = new URL(origin);
    return url.hostname.endsWith(".lovable.dev") || url.hostname.endsWith(".lovable.app");
  } catch {
    return false;
  }
}

/** Fire-and-forget postMessage that NEVER throws */
export function safePostMessage(message: any, targetOrigin?: string): void {
  try {
    // Only send when embedded in a parent frame
    if (typeof window === "undefined") return;
    if (window.parent === window) return; // not embedded

    const origin = targetOrigin || deriveParentOrigin();
    if (!origin) return; // can't determine safe target

    if (origin !== "*" && !isAllowedOrigin(origin)) return; // not in allowlist

    window.parent.postMessage(message, origin);
  } catch (err) {
    // Log once and swallow — never break app flow
    if (IS_DEV) console.warn("[bridge] postMessage failed (harmless):", (err as Error)?.message);
  }
}

function deriveParentOrigin(): string | null {
  try {
    if (document.referrer) {
      const ref = new URL(document.referrer).origin;
      if (isAllowedOrigin(ref)) return ref;
    }
  } catch { /* ignore */ }
  return null; // unknown parent — skip sending
}

export function setupBridgeABTest() {
  if (typeof window === "undefined") return;

  const urlMode = new URLSearchParams(window.location.search).get("bridge");
  const disabled = window.localStorage.getItem(DEBUG_DISABLE_LOVABLE_BRIDGE) === "1" || urlMode === "off";
  console.info(`[bridge] mode=${disabled ? "bridge_isolated" : "normal"}`);

  // Patch window.postMessage to wrap ALL calls safely (prevents DOMException from any caller)
  const originalPostMessage = window.postMessage.bind(window);
  window.postMessage = ((message: any, targetOrigin: any, transfer?: any) => {
    try {
      if (disabled && typeof targetOrigin === "string" &&
          (targetOrigin.includes("lovable.dev") || targetOrigin.includes("lovable.app"))) {
        return; // bridge disabled — silently skip
      }
      return originalPostMessage(message, targetOrigin, transfer);
    } catch (err) {
      // Swallow DOMException origin mismatch — never let it propagate
      if (IS_DEV) console.warn("[bridge] postMessage swallowed:", (err as Error)?.message);
    }
  }) as typeof window.postMessage;
}

export async function invokeEdgeFunction<T = any>(
  name: string,
  body: any,
  timeoutMs = 15_000
): Promise<T> {
  const id = `edge:${name}:${Date.now()}`;
  const startedAt = Date.now();
  pending.set(id, { label: `edge:${name}`, start: startedAt, requestId: id, routeTag: "invokeEdgeFunction" });

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("TIMEOUT"));
    }, timeoutMs);
  });

  try {
    console.log(`[invokeEdgeFunction] [${name}] START`, { requestId: id, timeoutMs });

    const callPromise = supabase.functions.invoke(name, { body }).then(({ data, error }) => {
      if (error) throw new Error(error.message || `Edge function ${name} failed`);
      if (data?.error) throw new Error(data.error);
      return data as T;
    });

    const data = await Promise.race([callPromise, timeoutPromise]);
    console.log(`[invokeEdgeFunction] [${name}] END`, {
      requestId: id,
      duration: Date.now() - startedAt,
    });
    return data;
  } catch (error: any) {
    const isTimeout = error?.message === "TIMEOUT";
    console.error(`[invokeEdgeFunction] [${name}] ${isTimeout ? "TIMEOUT" : "ERROR"}`, {
      requestId: id,
      duration: Date.now() - startedAt,
      message: error?.message || "Unknown error",
    });
    // Capture as admin warning
    try {
      const { captureWarning } = await import("@/lib/admin-warnings");
      captureWarning({
        page: window.location.pathname,
        message: `Edge Function "${name}": ${error?.message || "Unknown error"}`,
        details: { functionName: name, duration: Date.now() - startedAt, requestId: id },
      });
    } catch { /* silent */ }
    throw error;
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
    pending.delete(id);
  }
}
